import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  GetMyStatsResponse,
  GetSiteLeaderboardsResponse,
  LeaderboardWindow,
  ModelUsageLeaderboardEntry,
  SiteLeaderboardEntry,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { formatCoins, formatUsername } from '@fetmode/utils';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import './StatsPage.css';

type StatsTab = 'mine' | 'site';

function formatMetricValue(value: number, unit: 'coins' | 'minutes' | 'count'): string {
  if (unit === 'coins') {
    return formatCoins(value);
  }
  if (unit === 'minutes') {
    return t('common.stats.minutesValue').replace('{{count}}', String(value));
  }
  return String(value);
}

function ModelBoard({
  title,
  empty,
  entries,
}: {
  title: string;
  empty: string;
  entries: ModelUsageLeaderboardEntry[];
}): ReactElement {
  return (
    <section className="stats-page__board">
      <h2 className="stats-page__board-title">{title}</h2>
      {entries.length === 0 ? (
        <p className="stats-page__muted">{empty}</p>
      ) : (
        <ol className="stats-page__list">
          {entries.map((entry) => (
            <li key={entry.modelId} className="stats-page__row">
              <span className="stats-page__rank">#{entry.rank}</span>
              <div className="stats-page__row-main">
                <Link to={`/@${entry.username}`} className="stats-page__name">
                  {entry.displayName}
                </Link>
                <span className="stats-page__handle">{formatUsername(entry.username)}</span>
              </div>
              <span className="stats-page__value">
                {formatMetricValue(entry.value, entry.unit)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function SiteBoard({
  title,
  empty,
  entries,
}: {
  title: string;
  empty: string;
  entries: SiteLeaderboardEntry[];
}): ReactElement {
  return (
    <section className="stats-page__board">
      <h2 className="stats-page__board-title">{title}</h2>
      {entries.length === 0 ? (
        <p className="stats-page__muted">{empty}</p>
      ) : (
        <ol className="stats-page__list">
          {entries.map((entry) => (
            <li key={entry.userId} className="stats-page__row">
              <span className="stats-page__rank">#{entry.rank}</span>
              <div className="stats-page__row-main">
                <span className="stats-page__name">
                  {entry.displayName?.trim() || formatUsername(entry.username)}
                </span>
                <span className="stats-page__handle">{formatUsername(entry.username)}</span>
              </div>
              <span className="stats-page__value">
                {formatMetricValue(entry.value, entry.unit)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function StatsPage(): ReactElement {
  const { api } = useAuth();
  const [tab, setTab] = useState<StatsTab>('mine');
  const [window, setWindow] = useState<LeaderboardWindow>('all');
  const [mine, setMine] = useState<GetMyStatsResponse | null>(null);
  const [site, setSite] = useState<GetSiteLeaderboardsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [mineResult, siteResult] = await Promise.all([
        api.getMyStats(),
        api.getSiteLeaderboards(window),
      ]);
      setMine(mineResult);
      setSite(siteResult);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.stats.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [api, window]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="stats-page">
      <header className="stats-page__header">
        <h1 className="stats-page__title">{t('common.stats.title')}</h1>
        <p className="stats-page__hint">{t('common.stats.hint')}</p>
      </header>

      <div className="stats-page__tabs" role="tablist" aria-label={t('common.stats.title')}>
        {(
          [
            ['mine', t('common.stats.tabMine')],
            ['site', t('common.stats.tabSite')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={
              tab === id ? 'stats-page__tab stats-page__tab--active' : 'stats-page__tab'
            }
            onClick={() => {
              setTab(id);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="stats-page__muted">{t('common.stats.loading')}</p>
      ) : (
        <div className="stats-page__panel" role="tabpanel">
          {error ? <p className="fm-error">{error}</p> : null}

          {tab === 'mine' && mine ? (
            <>
              <section className="stats-page__summary" aria-label={t('common.stats.summary')}>
                <div className="stats-page__stat">
                  <span className="stats-page__stat-value">
                    {formatCoins(mine.summary.tipsGivenCoins)}
                  </span>
                  <span className="stats-page__stat-label">{t('common.stats.tipsGiven')}</span>
                </div>
                <div className="stats-page__stat">
                  <span className="stats-page__stat-value">
                    {t('common.stats.minutesValue').replace(
                      '{{count}}',
                      String(mine.summary.privateMinutes),
                    )}
                  </span>
                  <span className="stats-page__stat-label">{t('common.stats.privateMinutes')}</span>
                </div>
                <div className="stats-page__stat">
                  <span className="stats-page__stat-value">
                    {formatCoins(mine.summary.contentSpentCoins)}
                  </span>
                  <span className="stats-page__stat-label">{t('common.stats.contentSpent')}</span>
                </div>
                <div className="stats-page__stat">
                  <span className="stats-page__stat-value">{mine.summary.showsJoined}</span>
                  <span className="stats-page__stat-label">{t('common.stats.showsJoined')}</span>
                </div>
                <div className="stats-page__stat">
                  <span className="stats-page__stat-value">{mine.summary.fanClubs}</span>
                  <span className="stats-page__stat-label">{t('common.stats.fanClubs')}</span>
                </div>
              </section>

              <ModelBoard
                title={t('common.stats.topTips')}
                empty={t('common.stats.emptyTips')}
                entries={mine.topModelsByTips}
              />
              <ModelBoard
                title={t('common.stats.topPrivate')}
                empty={t('common.stats.emptyPrivate')}
                entries={mine.topModelsByPrivateMinutes}
              />
              <ModelBoard
                title={t('common.stats.topContent')}
                empty={t('common.stats.emptyContent')}
                entries={mine.topModelsByContent}
              />
            </>
          ) : null}

          {tab === 'site' ? (
            <>
              <div className="stats-page__windows" role="group" aria-label={t('common.stats.window')}>
                {(
                  [
                    ['all', t('common.stats.windowAll')],
                    ['week', t('common.stats.windowWeek')],
                    ['month', t('common.stats.windowMonth')],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={
                      window === id
                        ? 'stats-page__window stats-page__window--active'
                        : 'stats-page__window'
                    }
                    onClick={() => {
                      setWindow(id);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {site ? (
                <>
                  <SiteBoard
                    title={t('common.stats.siteTips')}
                    empty={t('common.stats.emptySite')}
                    entries={site.topTippers}
                  />
                  <SiteBoard
                    title={t('common.stats.sitePrivate')}
                    empty={t('common.stats.emptySite')}
                    entries={site.topPrivateMinutes}
                  />
                  <SiteBoard
                    title={t('common.stats.siteContent')}
                    empty={t('common.stats.emptySite')}
                    entries={site.topContentSpenders}
                  />
                  <SiteBoard
                    title={t('common.stats.siteTotal')}
                    empty={t('common.stats.emptySite')}
                    entries={site.topTotalSpend}
                  />
                </>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
